import { Point } from 'geojson';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { Business } from '../../backend/db/all-entities';
import { BusinessRegistrationData } from '../../pages/api/businesses';
import fullWidthCard from '../../styles/full-width-card.module.css';
import inputGrid from '../../styles/input-grid.module.css';
import { useRequest } from '../../utils/make-api-request';
import ButtonWithFeedback from '../button-with-feedback';
import CategoryChooser from '../category/chooser';
import ImageInput from '../inputs/image-input';
import { ImageUpload, useImageUpload } from '../inputs/image-input/helpers';
import LocationPicker from '../inputs/map-location-picker';
import SegmentedRadioControl from '../inputs/segmented-radio-control';
import { useErrorNotifier } from '../../utils/hooks';
import Password from '../inputs/password';
import ConfirmPassword from '../inputs/confirm-password';

interface PublicInformationFieldsetProps {
  name: string;
  setName: (name: string) => void;
  phoneNumber: string | null;
  setPhoneNumber: (phoneNumber: string | null) => void;
  bio: string;
  setBio: (bio: string) => void;
  publicEmail: string | null;
  setPublicEmail: (publicEmail: string | null) => void;
  websiteUrl: string | null;
  setWebsiteUrl: (websiteUrl: string | null) => void;
  logo: ImageUpload;
}

export const PublicInformationFieldset: React.FC<PublicInformationFieldsetProps> = ({
  name,
  setName,
  phoneNumber,
  setPhoneNumber,
  bio,
  setBio,
  publicEmail,
  setPublicEmail,
  websiteUrl,
  setWebsiteUrl,
  logo
}) => (
  <fieldset>
    <legend>Public Information</legend>
    <div className={inputGrid.inputGrid}>
      <input
        type='text'
        value={name}
        placeholder='Company or individual name'
        required
        onChange={e => setName(e.target.value)}
      />
      <input
        type='tel'
        value={phoneNumber ?? ''}
        placeholder='Phone number (optional)'
        onChange={e => setPhoneNumber(e.target.value || null)}
      />
      <input
        type='email'
        value={publicEmail ?? ''}
        placeholder='Public email (optional)'
        onChange={e => setPublicEmail(e.target.value || null)}
      />
      <input
        type='url'
        value={websiteUrl ?? ''}
        placeholder='Full website URL (optional)'
        onChange={e => setWebsiteUrl(e.target.value || null)}
        onBlur={e => {
          function isValidHttpUrl(string: string) {
            if (!string.includes('.')) {
              return false; // Technically, something like "https://foo" is a valid URL but users do not expect that.
            }
            try {
              const url = new URL(string);
              return url.protocol === 'http:' || url.protocol === 'https:';
            } catch {
              return false;
            }
          }
          if (!isValidHttpUrl(e.target.value) && isValidHttpUrl('https://' + e.target.value)) {
            setWebsiteUrl('https://' + e.target.value);
          }
        }}
      />
      <textarea
        className='font-normal h-24'
        name='description'
        placeholder='Bio (200 characters or less)'
        value={bio}
        maxLength={200}
        required
        onChange={e => setBio(e.target.value)}
      />
      <ImageInput imageUpload={logo} label='logo/photo' className='w-full h-24' profilePicture />
    </div>
  </fieldset>
);

const BusinessSignUpForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [type, setType] = useState<Business['type']>('company');
  const [fein, setFein] = useState('');
  const professionalCertification = useImageUpload();
  const [authorizedCategories, setAuthorizedCategories] = useState([] as string[]);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [bio, setBio] = useState('');
  const [publicEmail, setPublicEmail] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState<string | null>(null);
  const logo = useImageUpload();
  const [locations, setLocations] = useState([] as Point[]);

  const errorHandler = useErrorNotifier();
  const router = useRouter();
  const [submissionRequest, sendApplication] = useRequest('businesses', 'post');

  function submit() {
    const businessRegistration: BusinessRegistrationData = {
      business: {
        email,
        passwordHash: password,
        type,
        fein,
        name,
        phoneNumber,
        bio,
        publicEmail,
        websiteUrl,
        logoUrl: logo.imageUrl,
        geolocations: locations.map(l => ({ point: l }))
      },
      ...(authorizedCategories.length > 0
        ? {
            initialQualification: {
              categories: authorizedCategories,
              imageUrl: professionalCertification.imageUrl
            }
          }
        : {})
    };
    sendApplication(businessRegistration)
      .then(() => {
        router.push({ pathname: '/business-registration-successful' });
      })
      .catch(errorHandler);
  }
  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form
        className={fullWidthCard.fullWidthCard}
        onKeyDown={e => {
          if (e.key === 'Enter') e.preventDefault();
        }}
        onSubmit={e => {
          e.preventDefault();
          submit();
        }}
      >
        <h3 className='text-center'>Create an account</h3>
        <fieldset>
          <legend>Account Info</legend>
          <div className={inputGrid.inputGrid}>
            <input
              type='email'
              name='email'
              placeholder='Email'
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <Password
              name='password'
              placeholder='Password'
              required
              value={password}
              isNew
              onChange={e => setPassword(e.target.value)}
            />
            <ConfirmPassword name='confirm-password' placeholder='Confirm password' required mustMatch={password} />
            <div className='flex flex-col justify-end'>
              <div className='text-sm pb-1 leading-none select-none'>Account type</div>
              <SegmentedRadioControl
                selectedOption={type}
                setSelectedOption={option => setType(option)}
                options={{ individual: 'Individual', company: 'Company' }}
              />
            </div>
          </div>
        </fieldset>
        <fieldset>
          <legend>Qualifications</legend>
          <div className={inputGrid.inputGrid}>
            {type === 'company' && (
              <input
                type='number'
                maxLength={9}
                value={fein}
                placeholder='FEIN number'
                required
                onChange={e => setFein(e.target.value)}
              />
            )}
            {type === 'individual' && (
              <ImageInput
                imageUpload={professionalCertification}
                layout='horizontal'
                label='professional certification (optional)'
                className='w-full h-16 bg-gray-800'
                // required
              />
            )}
          </div>
          <div className='mt-2 mb-1 select-none'>
            {type === 'individual'
              ? 'The certification uploaded above authorizes me for the following categories:'
              : 'My company is authorized for the following categories:'}
          </div>
          <CategoryChooser
            multiple
            onlyShowCategoriesWhichRequireQualification
            selectedCategories={authorizedCategories}
            setSelectedCategories={setAuthorizedCategories}
          />
        </fieldset>
        <PublicInformationFieldset
          {...{
            name,
            setName,
            phoneNumber,
            setPhoneNumber,
            bio,
            setBio,
            publicEmail,
            setPublicEmail,
            websiteUrl,
            setWebsiteUrl,
            logo
          }}
        />
        <fieldset>
          <legend>Locations</legend>
          <div className='mb-1'>Click on the map to select locations. Click markers to remove them.</div>
          <LocationPicker locations={locations} setLocations={setLocations} min={1} max={5} />
        </fieldset>
        {/* <fieldset>
          <legend>Pricing</legend>
          <input
            type='radio'
            value='free'
            id='free'
            checked={pricingPlan === 'free'}
            onChange={e => setPricingPlan(e.target.value as BusinessRegistrationData['pricingPlan'])}
          />
          <label htmlFor='free'>Free</label>
          <p>Free pricing is all we currently support. Other tiers are coming soon!</p>
        </fieldset> */}
        <p className='text-sm mb-1'>Your account will need to approved before you can use it.</p>
        <div className='flex justify-end'>
          <ButtonWithFeedback className='primary ml-auto block' request={submissionRequest}>
            Register
          </ButtonWithFeedback>
        </div>
      </form>
    </>
  );
};

export default BusinessSignUpForm;
